<template>
  <div class="llm-flow-entry">
    <div v-if="isBuilderOpen !== true" class="llm-flow-launcher-card">
      <div class="llm-flow-launcher-copy">
        <span class="llm-flow-launcher-badge">Flow Builder</span>
        <h3 class="llm-flow-launcher-title">Design the conversation in a focused workspace</h3>
        <p class="llm-flow-launcher-description">
          Open the flow editor in fullscreen mode, manage the canvas in the center, and edit the selected step from the inspector on the right.
        </p>
        <div class="llm-flow-launcher-stats">
          <span class="llm-flow-stat-pill">
            <Icon icon="tabler:box" width="16" height="16" />
            {{ flowNodes.length }} steps
          </span>
          <span class="llm-flow-stat-pill">
            <Icon icon="tabler:git-branch" width="16" height="16" />
            {{ flowEdges.length }} connections
          </span>
        </div>
      </div>
      <div class="llm-flow-launcher-actions">
        <button class="btn btn-primary" @click="openFlowBuilder">Open Flow Builder</button>
        <button class="btn btn-outline" @click="organizeFlow">Organize</button>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="isBuilderOpen === true" class="llm-flow-fullscreen-shell">
        <div class="llm-flow-fullscreen-backdrop"></div>
        <div class="llm-flow-fullscreen" tabindex="0" @keydown="handleKeyDown">
          <header class="llm-flow-fullscreen-header">
            <div class="llm-flow-fullscreen-heading">
              <span class="llm-flow-launcher-badge">Flow Builder</span>
            </div>
            <div class="llm-flow-fullscreen-actions">
              <div class="llm-flow-launcher-stats llm-flow-launcher-stats--compact">
                <span class="llm-flow-stat-pill">
                  <Icon icon="tabler:box" width="16" height="16" />
                  {{ flowNodes.length }} steps
                </span>
                <span class="llm-flow-stat-pill">
                  <Icon icon="tabler:git-branch" width="16" height="16" />
                  {{ flowEdges.length }} connections
                </span>
              </div>
              <button class="btn btn-sm btn-outline" @click="centerCanvas">Center Canvas</button>
              <button class="btn btn-sm" @click="organizeFlow">{{ t('llmFlow.organizeFlowButton') }}</button>
              <button v-if="isElementSelected === true" class="btn btn-sm btn-error btn-outline" @click="requestDeleteSelectedElements">
                <Icon icon="tabler:trash" width="16" height="16" />
              </button>
              <button class="btn btn-sm btn-ghost" @click="handleBuilderClose">
                <Icon icon="tabler:x" width="18" height="18" />
              </button>
            </div>
          </header>

          <div class="llm-flow-fullscreen-body">
            <aside class="llm-flow-library">
              <div class="llm-flow-panel-header">
                <div>
                  <div class="llm-flow-panel-eyebrow">Library</div>
                  <h3>Nodes</h3>
                </div>
              </div>
              <WorkflowNodePalette
                :search-value="workflowNodeSearch"
                :filtered-groups="filteredSupportedNodeGroups"
                @update:searchValue="workflowNodeSearch = $event"
                @dragstart="onDragStart"
                @addnode="addSupportedNode"
              />
            </aside>

            <section class="llm-flow-stage">
              <div class="llm-flow-canvas-wrap">
                <VueFlow
                  :key="flowKey"
                  v-model="elements"
                  :fit-view-on-init="true"
                  :min-zoom="0.1"
                  :max-zoom="8"
                  :default-zoom="1"
                  :node-types="nodeTypes"
                  :multi-selection-key-code="isMultiSelectionKeyPressed"
                  :select-nodes-on-drag="true"
                  :zoom-on-scroll="true"
                  :zoom-on-pinch="true"
                  :zoom-on-double-click="true"
                  :pan-on-drag="true"
                  :delete-key-code="null"
                  class="llm-flow-canvas"
                  @node-click="handleNodeSelect"
                  @node-double-click="handleNodeDoubleClick"
                  @edge-click="handleEdgeClick"
                  @edge-double-click="handleEdgeDoubleClick"
                  @pane-click="clearInspector"
                  @dragover.prevent="() => {}"
                  @drop="onDrop"
                >
                  <Background />
                  <Controls>
                    <ControlButton :title="t('llmFlow.deleteSelectedButton')" @click="requestDeleteSelectedElements">
                      <Icon icon="tabler:trash" width="16" height="16" />
                    </ControlButton>
                  </Controls>

                  <template #node-root="props">
                    <div class="vue-flow__node-root vue-flow__node-default" :connectable="false">
                      <div class="node-type-tag">
                        {{ (props.data.nodeType !== undefined && props.data.nodeType !== null && props.data.nodeType !== '') ? (props.data.nodeType === 'Root' ? 'Start Conversation' : props.data.nodeType) : 'Start Conversation' }}
                      </div>
                      <div>
                        {{ (props.data.label !== undefined && props.data.label !== null && props.data.label !== '') ? props.data.label : 'We starts here!' }}
                      </div>
                      <div v-if="getCanvasNodeSummary(props.data) !== ''" class="node-description">
                        {{ getCanvasNodeSummary(props.data) }}
                      </div>
                      <Handle v-if="getCanvasAllowsOutgoing(props.data) === true" type="source" :position="Position.Right" />
                    </div>
                  </template>

                  <template #node-default="props">
                    <div class="custom-node-padding">
                      <Handle type="target" :position="Position.Left" />
                      <div class="node-type-tag">{{ getCanvasNodeTypeLabel(props.data) }}</div>
                      <div>{{ props.data.label }}</div>
                      <div v-if="getCanvasNodeSummary(props.data) !== ''" class="node-description">
                        {{ getCanvasNodeSummary(props.data) }}
                      </div>
                      <Handle type="source" :position="Position.Right" />
                    </div>
                  </template>

                  <template #edge-smoothstep="props">
                    <SmoothStepEdge
                      :key="props.id + '-' + props.label"
                      :id="props.id"
                      :source-x="props.sourceX"
                      :source-y="props.sourceY"
                      :target-x="props.targetX"
                      :target-y="props.targetY"
                      :source-position="props.sourcePosition"
                      :target-position="props.targetPosition"
                      :label="props.label"
                      :markerEnd="props.markerEnd"
                      :style="props.style"
                    />

                    <EdgeLabelRenderer>
                      <div
                        v-if="props.label !== undefined && props.label !== null && props.label.trim().length > 0"
                        :key="props.id + '-' + (props.label !== undefined && props.label !== null && props.label !== '' ? props.label : 'empty') + '-labeldiv'"
                        :style="{
                          position: 'absolute',
                          transform: `translate(-50%, -50%) translate(${(props.sourceX + props.targetX) / 2}px,${(props.sourceY + props.targetY) / 2}px)`,
                          pointerEvents: 'all',
                          zIndex: 20
                        }"
                        class="edge-condition-card"
                        @click.stop="handleEdgeClick({ edge: props, event: $event })"
                        @dblclick.stop="handleEdgeDoubleClick({ edge: props, event: $event })"
                      >
                        {{ props.label }}
                      </div>
                      <div
                        v-else
                        :key="props.id + '-empty-labeldiv'"
                        :style="{
                          position: 'absolute',
                          transform: `translate(-50%, -50%) translate(${(props.sourceX + props.targetX) / 2}px,${(props.sourceY + props.targetY) / 2}px)`,
                          pointerEvents: 'all',
                          zIndex: 20
                        }"
                        class="edge-condition-card no-condition"
                        @click.stop="handleEdgeClick({ edge: props, event: $event })"
                        @dblclick.stop="handleEdgeDoubleClick({ edge: props, event: $event })"
                      >
                        {{ t('llmFlow.noConditionSet') }}
                      </div>
                    </EdgeLabelRenderer>
                  </template>
                </VueFlow>
              </div>
            </section>

            <aside class="llm-flow-inspector">
              <div class="llm-flow-panel-header">
                <div>
                  <div class="llm-flow-panel-eyebrow">Inspector</div>
                  <h3>{{ inspectorTitle }}</h3>
                </div>
                <button v-if="inspectorMode !== 'none'" class="btn btn-xs btn-ghost" @click="clearInspector">
                  <Icon icon="tabler:x" width="14" height="14" />
                </button>
              </div>

              <div class="llm-flow-inspector-body">
                <div v-if="inspectorMode === 'question'" class="llm-flow-inspector-stack">
                  <div class="llm-flow-selection-card">
                    <div class="llm-flow-selection-card-top">
                      <span class="llm-flow-selection-badge">{{ selectedNodeTypeLabel }}</span>
                      <span class="llm-flow-selection-meta">{{ selectedNodeMeta }}</span>
                    </div>
                    <h4>{{ selectedNodeTitle }}</h4>
                    <p>{{ selectedNodeSummary }}</p>
                  </div>

                  <StepEditorModal
                    :is-open="true"
                    render-mode="panel"
                    :node-data="editingNodeData"
                    :available-tools="availableTools"
                    :all-flow-nodes="flowNodes"
                    @close="closeStepModal"
                    @save="saveStepData"
                  />
                </div>

                <div v-else-if="inspectorMode === 'action'" class="llm-flow-inspector-stack">
                  <div class="llm-flow-selection-card">
                    <div class="llm-flow-selection-card-top">
                      <span class="llm-flow-selection-badge">{{ selectedNodeTypeLabel }}</span>
                      <span class="llm-flow-selection-meta">{{ selectedNodeMeta }}</span>
                    </div>
                    <h4>{{ selectedNodeTitle }}</h4>
                    <p>{{ selectedNodeSummary }}</p>
                  </div>

                  <ActionStepEditorModal
                    :is-open="true"
                    render-mode="panel"
                    :node-data="editingNodeData"
                    :available-tools="availableTools"
                    :all-flow-nodes="flowNodes"
                    @close="closeActionStepModal"
                    @save="saveStepData"
                  />
                </div>

                <div v-else-if="inspectorMode === 'edge'" class="llm-flow-edge-panel">
                  <div class="form-control mb-4">
                    <label class="label">
                      <span class="label-text">Condition Prompt</span>
                    </label>
                    <input
                      v-model="currentEdgeCondition"
                      type="text"
                      class="input input-bordered w-full"
                      placeholder="Leave empty for unconditional flow"
                    />
                    <div class="text-xs text-gray-500 mt-2">
                      Use a clear natural-language rule so the engine can route the conversation correctly.
                    </div>
                  </div>
                  <div class="llm-flow-edge-panel-actions">
                    <button class="btn btn-error btn-outline" @click="handleDeleteEdgeFromModal">Delete Connection</button>
                    <button class="btn" @click="saveEdgeCondition(currentEdgeCondition)">Save Condition</button>
                  </div>
                </div>

                <div v-else class="llm-flow-empty-inspector">
                  <div class="llm-flow-empty-inspector-icon">
                    <Icon icon="tabler:layout-sidebar-right-collapse" width="28" height="28" />
                  </div>
                  <h4>Select something to edit</h4>
                  <p>
                    Click a step to edit its configuration here, or click a connection label to manage its routing condition.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <dialog
        :open="isDeleteConfirmOpen === true"
        class="modal llm-flow-delete-confirm"
        :class="{ 'modal-open': isDeleteConfirmOpen === true }"
      >
        <div class="modal-box">
          <h3 class="font-bold text-lg">{{ t('llmFlow.confirmDeletionTitle') }}</h3>
          <p class="py-4">{{ t('llmFlow.confirmDeletionMessage', { nodes: elementsToDelete.nodes.length, connections: elementsToDelete.edges.length }, `Are you sure you want to delete the selected ${elementsToDelete.nodes.length} step(s) and ${elementsToDelete.edges.length} connection(s)?`) }}</p>
          <div class="modal-action">
            <button class="btn mr-2" @click="cancelDelete">{{ t('llmFlow.cancelButton') }}</button>
            <button class="btn btn-error ml-2" @click="confirmDelete">{{ t('llmFlow.deleteButton') }}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button @click="cancelDelete">{{ t('llmFlow.closeButton') }}</button>
        </form>
      </dialog>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch, nextTick, defineExpose, shallowRef } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { VueFlow, useVueFlow, SmoothStepEdge, EdgeLabelRenderer, Handle, Position, MarkerType } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls, ControlButton } from '@vue-flow/controls';
import StepEditorModal from './StepEditorModal.vue';
import ActionStepEditorModal from './ActionStepEditorModal.vue';
import axios from '../api/axiosInstance';
import { globalState } from '@/globalState';
import QuestionNode from './nodes/QuestionNode.vue';
import ActionNode from './nodes/ActionNode.vue';
import RootNode from './nodes/RootNode.vue';
import KnowledgeBaseNode from './nodes/KnowledgeBaseNode.vue';
import IfNode from './nodes/IfNode.vue';
import WorkflowNodePalette from './workflowEditor/WorkflowNodePalette.vue';
import { useI18n } from '@/i18n';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const { 
  addNodes, 
  onConnect, 
  addEdges, 
  updateNode, 
  findNode, 
  removeNodes, 
  removeEdges, 
  getSelectedElements, 
  getSelectedNodes, 
  getSelectedEdges,
  findEdge,     
  setNodes,
  setEdges,
  addSelectedNodes,
  addSelectedEdges,
  fitView,
  project
} = useVueFlow();
const apiEndpoint = reactive(globalState.apiEndpoint);
const { t } = useI18n();
const props = defineProps({
  startingMessage: {
    type: [String, Object],
    default: ''
  }
});
const emit = defineEmits(['builder-closed', 'update:startingMessage']);

let id = 0;
const elements = ref([]); 
const isBuilderOpen = ref(false);
const inspectorMode = ref('none');
const editingNodeId = ref(null);
const editingNodeData = ref({}); 
const availableTools = ref([]);
const isDeleteConfirmOpen = ref(false);
const elementsToDelete = ref({ nodes: [], edges: [] });
const editingEdgeId = ref(null);    
const currentEdgeCondition = ref('');
const flowKey = ref(0);

const dragData = ref(null);

const nodeTypes = shallowRef({
  Question: QuestionNode,
  Action: ActionNode,
  Root: RootNode,
  KnowledgeBase: KnowledgeBaseNode,
  If: IfNode,
});

function getToolNodePayload(tool) {
  if (tool !== null && tool !== undefined && typeof tool === 'object') {
    if (tool.toolData !== null && tool.toolData !== undefined && typeof tool.toolData === 'object') {
      return tool.toolData;
    }
    if (tool.tooldata !== null && tool.tooldata !== undefined && typeof tool.tooldata === 'object') {
      return tool.tooldata;
    }
    if (typeof tool.toolData === 'string') {
      try {
        const parsedToolData = JSON.parse(tool.toolData);
        if (parsedToolData !== null && parsedToolData !== undefined && typeof parsedToolData === 'object') {
          return parsedToolData;
        }
      } catch (error) {
      }
    }
    if (typeof tool.tooldata === 'string') {
      try {
        const parsedToolData = JSON.parse(tool.tooldata);
        if (parsedToolData !== null && parsedToolData !== undefined && typeof parsedToolData === 'object') {
          return parsedToolData;
        }
      } catch (error) {
      }
    }
  }
  return {};
}

function getToolNodeId(tool) {
  if (tool === undefined || tool === null || typeof tool !== 'object') {
    return null;
  }
  const rawValue = tool.toolId !== undefined ? tool.toolId : (tool.toolid !== undefined ? tool.toolid : tool.id);
  const parsedValue = Number.parseInt(String(rawValue), 10);
  if (Number.isFinite(parsedValue) !== true || parsedValue <= 0) {
    return null;
  }
  return parsedValue;
}

function getToolNodeName(tool) {
  if (tool === undefined || tool === null || typeof tool !== 'object') {
    return 'Tool';
  }
  const rawValue = tool.toolName !== undefined ? tool.toolName : (tool.toolname !== undefined ? tool.toolname : tool.name);
  if (rawValue !== undefined && rawValue !== null && String(rawValue).trim().length > 0) {
    return String(rawValue).trim();
  }
  const toolId = getToolNodeId(tool);
  if (toolId !== null) {
    return `Tool ${toolId}`;
  }
  return 'Tool';
}

function getToolNodeDescription(tool) {
  const payload = getToolNodePayload(tool);
  const descriptionCandidates = [
    tool !== undefined && tool !== null ? tool.description : '',
    tool !== undefined && tool !== null ? tool.toolDescription : '',
    tool !== undefined && tool !== null ? tool.tooldescription : '',
    payload.description,
    payload.shortDescription
  ];
  for (let i = 0; i < descriptionCandidates.length; i = i + 1) {
    const currentValue = typeof descriptionCandidates[i] === 'string' ? descriptionCandidates[i].trim() : '';
    if (currentValue.length > 0) {
      return currentValue;
    }
  }
  return `Run ${getToolNodeName(tool)}`;
}

function getToolNodeImage(tool) {
  const payload = getToolNodePayload(tool);
  const imageCandidates = [
    payload.integrationImage,
    payload.image,
    payload.logoUrl,
    tool !== undefined && tool !== null ? tool.imageUrl : '',
    tool !== undefined && tool !== null ? tool.image : '',
    tool !== undefined && tool !== null ? tool.logoUrl : ''
  ];
  for (let i = 0; i < imageCandidates.length; i = i + 1) {
    const currentValue = typeof imageCandidates[i] === 'string' ? imageCandidates[i].trim() : '';
    if (currentValue.length > 0) {
      return currentValue;
    }
  }
  return '';
}

function getToolNodeIcon(tool) {
  const payload = getToolNodePayload(tool);
  const iconCandidates = [
    payload.icon,
    payload.integrationIcon,
    tool !== undefined && tool !== null ? tool.icon : '',
    tool !== undefined && tool !== null ? tool.integrationIcon : ''
  ];
  for (let i = 0; i < iconCandidates.length; i = i + 1) {
    const currentValue = typeof iconCandidates[i] === 'string' ? iconCandidates[i].trim() : '';
    if (currentValue.length > 0) {
      return currentValue;
    }
  }
  return 'tabler:tool';
}

const availableNodes = [
  {
    type: 'Question',
    label: 'Question Step',
    description: 'Asks the user a question and can collect variables or use tools.',
    initialData: { label: 'Question Step', message: '', variablesToCollect: [], toolsToUse: [], buttons: [], useExactMessage: false }
  },
  {
    type: 'Action',
    label: 'Action Step',
    description: 'Performs an action using a configured tool.',
    initialData: { label: 'Action Step', message: '', stepDescription: '', actionType: 'activateTool', toolSource: 'storedTool', selectedToolId: null, inlineTool: { toolName: '', description: '', requiredParams: [], optionalParams: [], functionConfig: '' }, toolInputMappings: {}, variablesToCollect: [], useExactMessage: false, config: {} }
  },
  {
    type: 'KnowledgeBase',
    label: 'Knowledge base step',
    description: 'Queries the agent\'s general knowledge base. Transitions based on query + KB answer.',
    initialData: { label: 'Knowledge base step' }
  },
  {
    type: 'If',
    label: 'If Condition',
    description: 'Checks a variable value and routes to another node.',
    initialData: { label: 'If Condition', stepDescription: '', config: { sourceType: 'variable', sourceKey: '', operator: 'equals', left: '', right: '' } }
  },
  {
    type: 'Action',
    actionType: 'httpRequest',
    label: 'HTTP Request',
    description: 'Calls external HTTP endpoint and saves response variables.',
    initialData: {
      label: 'HTTP Request',
      message: '',
      stepDescription: '',
      actionType: 'httpRequest',
      selectedToolId: null,
      toolInputMappings: {},
      variablesToCollect: [],
      useExactMessage: false,
      config: {
        method: 'POST',
        url: '',
        headersList: [],
        body: '{}',
        outputVariable: '',
        responseMappings: []
      }
    }
  }
];

const supportedBaseNodeGroups = [
  {
    id: 'conversation',
    label: 'Conversation',
    nodes: [
      {
        id: 'flow-question',
        type: 'Question',
        label: 'Question Step',
        description: 'Ask the user, collect variables, and continue naturally.',
        icon: 'tabler:message-circle',
        data: { label: 'Question Step', message: '', variablesToCollect: [], toolsToUse: [], buttons: [], useExactMessage: false, nodeType: 'Question' }
      },
      {
        id: 'flow-knowledge-base',
        type: 'KnowledgeBase',
        label: 'Knowledge Base Step',
        description: 'Answer from the knowledge base and continue the flow.',
        icon: 'tabler:database-search',
        data: { label: 'Knowledge base step', nodeType: 'KnowledgeBase' }
      }
    ]
  },
  {
    id: 'logic',
    label: 'Logic',
    nodes: [
      {
        id: 'flow-if-condition',
        type: 'If',
        label: 'If Condition',
        description: 'Check a variable value and route to the next node.',
        icon: 'tabler:git-branch',
        data: { label: 'If Condition', stepDescription: '', nodeType: 'If', config: { sourceType: 'variable', sourceKey: '', operator: 'equals', left: '', right: '' } }
      }
    ]
  },
  {
    id: 'actions',
    label: 'Actions',
    nodes: [
      {
        id: 'flow-action-tool',
        type: 'Action',
        label: 'Action Step',
        description: 'Run an existing tool and continue.',
        icon: 'tabler:tool',
        data: { label: 'Action Step', message: '', stepDescription: '', actionType: 'activateTool', toolSource: 'storedTool', selectedToolId: null, inlineTool: { toolName: '', description: '', requiredParams: [], optionalParams: [], functionConfig: '' }, toolInputMappings: {}, variablesToCollect: [], useExactMessage: false, nodeType: 'Action', config: {} }
      },
      {
        id: 'flow-action-http',
        type: 'Action',
        label: 'HTTP Request',
        description: 'Call endpoint, map response fields, and continue.',
        icon: 'tabler:world-www',
        data: {
          label: 'HTTP Request',
          message: '',
          stepDescription: '',
          actionType: 'httpRequest',
          selectedToolId: null,
          toolInputMappings: {},
          variablesToCollect: [],
          useExactMessage: false,
          nodeType: 'Action',
          config: {
            method: 'POST',
            url: '',
            headersList: [],
            body: '{}',
            outputVariable: '',
            responseMappings: []
          }
        }
      },
      {
        id: 'flow-action-announce',
        type: 'Action',
        label: 'Announce',
        description: 'Say a configured announcement before continuing.',
        icon: 'tabler:speakerphone',
        data: {
          label: 'Announce',
          message: '',
          stepDescription: '',
          actionType: 'announce',
          selectedToolId: null,
          toolInputMappings: {},
          variablesToCollect: [],
          useExactMessage: true,
          nodeType: 'Action',
          config: {
            allowUserInterrupt: true
          }
        }
      },
      {
        id: 'flow-action-transfer-call',
        type: 'Action',
        label: 'Transfer Call',
        description: 'Transfer the live call to a configured destination.',
        icon: 'tabler:phone-outgoing',
        data: {
          label: 'Transfer Call',
          message: '',
          stepDescription: 'Transfer the live call to another person, team, phone number, or SIP destination.',
          actionType: 'transferCall',
          selectedToolId: null,
          toolInputMappings: {},
          variablesToCollect: [],
          useExactMessage: false,
          nodeType: 'Action',
          config: {
            destination: '',
            delayMode: 'afterAssistantReply',
            delayMs: 0
          }
        }
      },
      {
        id: 'flow-action-end-call',
        type: 'Action',
        label: 'End Call',
        description: 'End the live call after the final message.',
        icon: 'tabler:phone-off',
        data: {
          label: 'End Call',
          message: '',
          stepDescription: 'End the live call when the flow is complete or the caller asks to hang up.',
          actionType: 'endCall',
          selectedToolId: null,
          toolInputMappings: {},
          variablesToCollect: [],
          useExactMessage: false,
          nodeType: 'Action',
          config: {
            delayMode: 'afterAssistantReply',
            delayMs: 0
          }
        }
      }
    ]
  }
];

const toolNodeGroup = computed(() => {
  const toolNodes = [];
  const toolsArray = Array.isArray(availableTools.value) === true ? availableTools.value : [];
  for (let i = 0; i < toolsArray.length; i = i + 1) {
    const tool = toolsArray[i];
    const toolId = getToolNodeId(tool);
    if (toolId === null) {
      continue;
    }
    const toolName = getToolNodeName(tool);
    const toolDescription = getToolNodeDescription(tool);
    toolNodes.push({
      id: `flow-tool-${toolId}`,
      type: 'Action',
      label: toolName,
      description: toolDescription,
      icon: getToolNodeIcon(tool),
      image: getToolNodeImage(tool),
      data: {
        label: toolName,
        message: '',
        stepDescription: toolDescription,
        actionType: 'activateTool',
        toolSource: 'storedTool',
        selectedToolId: toolId,
        inlineTool: { toolName: '', description: '', requiredParams: [], optionalParams: [], functionConfig: '' },
        toolInputMappings: {},
        variablesToCollect: [],
        useExactMessage: false,
        nodeType: 'Action',
        config: {}
      }
    });
  }
  if (toolNodes.length === 0) {
    return null;
  }
  return {
    id: 'tools',
    label: 'Tools',
    nodes: toolNodes
  };
});

const supportedNodeGroups = computed(() => {
  const groups = [];
  for (let i = 0; i < supportedBaseNodeGroups.length; i = i + 1) {
    groups.push(supportedBaseNodeGroups[i]);
  }
  if (toolNodeGroup.value !== null) {
    groups.push(toolNodeGroup.value);
  }
  return groups;
});

const workflowNodeSearch = ref('');
const filteredSupportedNodeGroups = computed(() => {
  const searchValue = String(workflowNodeSearch.value !== undefined && workflowNodeSearch.value !== null ? workflowNodeSearch.value : '').trim().toLowerCase();
  if (searchValue.length === 0) {
    return supportedNodeGroups.value;
  }
  const groups = [];
  for (let i = 0; i < supportedNodeGroups.value.length; i = i + 1) {
    const group = supportedNodeGroups.value[i];
    const nodes = Array.isArray(group.nodes) === true ? group.nodes : [];
    const matchedNodes = [];
    for (let j = 0; j < nodes.length; j = j + 1) {
      const node = nodes[j];
      const label = String(node.label !== undefined && node.label !== null ? node.label : '').toLowerCase();
      const description = String(node.description !== undefined && node.description !== null ? node.description : '').toLowerCase();
      if (label.includes(searchValue) === true || description.includes(searchValue) === true) {
        matchedNodes.push(node);
      }
    }
    if (matchedNodes.length > 0) {
      groups.push({ ...group, nodes: matchedNodes });
    }
  }
  return groups;
});

const flowNodes = computed(() => {
  return elements.value.filter(el => el.position !== undefined && el.position !== null);
});

const flowEdges = computed(() => {
  return elements.value.filter(el => el.source !== undefined && el.source !== null);
});

const isElementSelected = computed(() => {
    return getSelectedNodes.value.length > 0 || getSelectedEdges.value.length > 0;
});

const inspectorTitle = computed(() => {
  if (inspectorMode.value === 'action') {
    if (editingNodeData.value !== null && editingNodeData.value !== undefined && editingNodeData.value.toolSource === 'inlineJsTool') {
      return 'Edit JS Snippet';
    }
    return 'Edit Action Step';
  }
  if (inspectorMode.value === 'question') {
    return 'Edit Step';
  }
  if (inspectorMode.value === 'edge') {
    return 'Edit Connection';
  }
  return 'Inspector';
});

const selectedNodeTitle = computed(() => {
  if (editingNodeData.value !== null && editingNodeData.value !== undefined && typeof editingNodeData.value.label === 'string' && editingNodeData.value.label.trim().length > 0) {
    return editingNodeData.value.label.trim();
  }
  return 'Untitled step';
});

const selectedNodeTypeLabel = computed(() => {
  const nodeData = editingNodeData.value !== null && editingNodeData.value !== undefined ? editingNodeData.value : {};
  if (nodeData.nodeType === 'Root') {
    return 'Start';
  }
  if (nodeData.nodeType === 'KnowledgeBase') {
    return 'Knowledge Base';
  }
  if (nodeData.nodeType === 'If') {
    return 'If';
  }
  if (nodeData.nodeType === 'Action') {
    if (nodeData.actionType === 'transferCall') {
      return 'Transfer Call';
    }
    if (nodeData.actionType === 'endCall') {
      return 'End Call';
    }
    if (nodeData.actionType === 'announce') {
      return 'Announce';
    }
    if (nodeData.actionType === 'httpRequest') {
      return 'HTTP Request';
    }
    if (nodeData.toolSource === 'inlineJsTool') {
      return 'JS Snippet';
    }
    return 'Action';
  }
  if (nodeData.nodeType === 'Question') {
    return 'Question';
  }
  return 'Step';
});

const selectedNodeMeta = computed(() => {
  const nodeData = editingNodeData.value !== null && editingNodeData.value !== undefined ? editingNodeData.value : {};
  const variables = Array.isArray(nodeData.variablesToCollect) === true ? nodeData.variablesToCollect.length : 0;
  if (nodeData.nodeType === 'Action') {
    if (nodeData.actionType === 'transferCall') {
      const destination = nodeData.config !== undefined && nodeData.config !== null && typeof nodeData.config.destination === 'string' ? nodeData.config.destination.trim() : '';
      return destinationHasPhoneIpPort(destination) === true ? `To ${destination}` : 'Call transfer';
    }
    if (nodeData.actionType === 'endCall') {
      return 'Call ending';
    }
    if (nodeData.actionType === 'announce') {
      return nodeData.config !== undefined && nodeData.config !== null && nodeData.config.allowUserInterrupt === false ? 'Locked announcement' : 'Interruptible announcement';
    }
    if (nodeData.toolSource === 'inlineJsTool') {
      const rawCode = nodeData.inlineTool !== undefined && nodeData.inlineTool !== null && typeof nodeData.inlineTool.functionConfig === 'string' ? nodeData.inlineTool.functionConfig.trim() : '';
      const lineCount = rawCode.length > 0 ? rawCode.split('\n').length : 0;
      if (lineCount === 1) {
        return '1 line of code';
      }
      return `${lineCount} lines of code`;
    }
    return `${variables} variables`;
  }
  if (nodeData.nodeType === 'KnowledgeBase') {
    return variables > 0 ? `${variables} follow-up fields` : 'Knowledge response';
  }
  if (nodeData.nodeType === 'If') {
    const config = nodeData.config !== undefined && nodeData.config !== null ? nodeData.config : {};
    const sourceKey = typeof config.sourceKey === 'string' ? config.sourceKey.trim() : '';
    if (sourceKey.length > 0) {
      return `Checks ${sourceKey}`;
    }
    return 'Variable check';
  }
  return `${variables} collected fields`;
});

const selectedNodeSummary = computed(() => {
  const nodeData = editingNodeData.value !== null && editingNodeData.value !== undefined ? editingNodeData.value : {};
  if (typeof nodeData.stepDescription === 'string' && nodeData.stepDescription.trim().length > 0) {
    return nodeData.stepDescription.trim();
  }
  if (typeof nodeData.message === 'string' && nodeData.message.trim().length > 0) {
    return nodeData.message.trim();
  }
  if (nodeData.nodeType === 'Action' && nodeData.actionType === 'transferCall') {
    return 'This step transfers the live call to the configured destination.';
  }
  if (nodeData.nodeType === 'Action' && nodeData.actionType === 'endCall') {
    return 'This step ends the live call.';
  }
  if (nodeData.nodeType === 'Action' && nodeData.toolSource === 'inlineJsTool') {
    return 'This step runs custom JavaScript directly inside the flow runtime with Monaco editing in the inspector.';
  }
  if (nodeData.nodeType === 'Action' && nodeData.actionType === 'httpRequest') {
    return 'This step performs an external HTTP call and maps response fields back into the flow.';
  }
  if (nodeData.nodeType === 'KnowledgeBase') {
    return 'This step answers from the knowledge base and can route the conversation based on the reply.';
  }
  if (nodeData.nodeType === 'If') {
    return 'This step checks a collected variable and automatically routes to the matching next node.';
  }
  return 'Configure the selected step from the panel below.';
});

function getShortCanvasText(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  if (text.length <= 90) {
    return text;
  }
  return `${text.slice(0, 87)}...`;
}

function destinationHasPhoneIpPort(destination) {
  const value = typeof destination === 'string' ? destination.trim() : '';
  if (value.length === 0) {
    return false;
  }
  const atIndex = value.indexOf('@');
  const phoneCandidate = atIndex > 0 ? value.slice(0, atIndex) : value;
  const phoneDigits = phoneCandidate.replace(/\D/g, '');
  if (phoneDigits.length < 7) {
    return false;
  }
  if (/\d{1,3}[.]\d{1,3}[.]\d{1,3}[.]\d{1,3}/.test(value) !== true) {
    return false;
  }
  if (/:\d{2,5}\b/.test(value) !== true) {
    return false;
  }
  return true;
}

function getCanvasNodeTypeLabel(nodeData) {
  if (nodeData === undefined || nodeData === null) {
    return 'Step';
  }
  if (nodeData.nodeType === 'Question') {
    return 'Question Step';
  }
  if (nodeData.nodeType === 'Action') {
    if (nodeData.actionType === 'transferCall') {
      return 'Transfer Call';
    }
    if (nodeData.actionType === 'endCall') {
      return 'End Call';
    }
    if (nodeData.actionType === 'announce') {
      return 'Announce';
    }
    if (nodeData.actionType === 'httpRequest') {
      return 'HTTP Request';
    }
    if (nodeData.toolSource === 'inlineJsTool') {
      return 'JS Snippet';
    }
    return 'Action Step';
  }
  if (nodeData.nodeType === 'KnowledgeBase') {
    return 'Knowledge base step';
  }
  if (nodeData.nodeType === 'If') {
    return 'If Condition';
  }
  if (nodeData.nodeType === 'Root') {
    return 'Start Conversation';
  }
  return nodeData.nodeType !== undefined && nodeData.nodeType !== null && nodeData.nodeType !== '' ? nodeData.nodeType : 'Step';
}

function getCanvasNodeSummary(nodeData) {
  if (nodeData === undefined || nodeData === null) {
    return '';
  }
  const description = getShortCanvasText(nodeData.stepDescription);
  const message = getShortCanvasText(nodeData.message);
  if (nodeData.nodeType === 'Question') {
    const parts = [];
    if (description.length > 0) {
      parts.push(`Description: ${description}`);
    }
    if (message.length > 0) {
      parts.push(`Message: ${message}`);
    }
    return parts.join('\n');
  }
  if (nodeData.nodeType === 'Root' && message.length > 0) {
    return `Starts with: ${message}`;
  }
  if (nodeData.actionType === 'transferCall') {
    const destination = nodeData.config !== undefined && nodeData.config !== null && typeof nodeData.config.destination === 'string' ? nodeData.config.destination.trim() : '';
    if (destinationHasPhoneIpPort(destination) === true) {
      return `Transfer to ${destination}`;
    }
    return '';
  }
  if (nodeData.nodeType === 'If') {
    const config = nodeData.config !== undefined && nodeData.config !== null ? nodeData.config : {};
    const sourceKey = typeof config.sourceKey === 'string' ? config.sourceKey.trim() : '';
    const left = typeof config.left === 'string' ? config.left.trim() : '';
    const right = typeof config.right === 'string' ? config.right.trim() : '';
    const operator = typeof config.operator === 'string' && config.operator.trim().length > 0 ? config.operator.trim() : 'equals';
    const subject = sourceKey.length > 0 ? sourceKey : left;
    if (subject.length === 0) {
      return 'Configure a variable check';
    }
    if (right.length === 0) {
      return `${subject} ${operator}`;
    }
    return `${subject} ${operator} ${right}`;
  }
  if (description.length > 0 && message.length > 0) {
    return `Description: ${description}\nMessage: ${message}`;
  }
  if (description.length > 0) {
    return `Description: ${description}`;
  }
  if (message.length > 0) {
    if (nodeData.nodeType === 'Root') {
      return `Starts with: ${message}`;
    }
    return `Message: ${message}`;
  }
  if (nodeData.actionType === 'httpRequest') {
    const method = nodeData.config !== undefined && nodeData.config !== null && typeof nodeData.config.method === 'string' ? nodeData.config.method : 'POST';
    const url = nodeData.config !== undefined && nodeData.config !== null && typeof nodeData.config.url === 'string' ? nodeData.config.url.trim() : '';
    if (url.length > 0) {
      return `Method: ${method}\nURL: ${url}`;
    }
    return `Method: ${method}`;
  }
  if (nodeData.actionType === 'announce') {
    return nodeData.config !== undefined && nodeData.config !== null && nodeData.config.allowUserInterrupt === false ? 'Locked announcement' : 'Can be stopped by talking';
  }
  const variables = Array.isArray(nodeData.variablesToCollect) === true ? nodeData.variablesToCollect.length : 0;
  if (variables > 0) {
    return `Collects ${variables} field${variables === 1 ? '' : 's'}`;
  }
  return '';
}

function getCanvasAllowsOutgoing(nodeData) {
  if (nodeData !== undefined && nodeData !== null && nodeData.actionType === 'endCall') {
    return false;
  }
  return true;
}

function getNextId() {
  return `step-${id++}`;
}

function getStartingMessageText() {
  if (typeof props.startingMessage === 'string') {
    return props.startingMessage.trim();
  }
  if (props.startingMessage !== null && props.startingMessage !== undefined && typeof props.startingMessage === 'object') {
    const candidateKeys = ['default', 'en', 'he', 'ar'];
    for (let i = 0; i < candidateKeys.length; i = i + 1) {
      const value = props.startingMessage[candidateKeys[i]];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    const keys = Object.keys(props.startingMessage);
    for (let i = 0; i < keys.length; i = i + 1) {
      const value = props.startingMessage[keys[i]];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
  }
  return '';
}

function buildStartingMessagePayload(message) {
  const normalizedMessage = typeof message === 'string' ? message : '';
  if (props.startingMessage !== null && props.startingMessage !== undefined && typeof props.startingMessage === 'object' && Array.isArray(props.startingMessage) === false) {
    return {
      ...props.startingMessage,
      default: normalizedMessage
    };
  }
  return {
    default: normalizedMessage
  };
}

function createBaseNodeData(label, nodeType = 'Question', isRoot = false) {
  const baseData = {
    label: label,
    stepDescription: '',
    nodeType: nodeType
  };

  if (isRoot === true) {
    return {
      ...baseData,
      nodeType: 'Root',
      isRoot: true,
      label: 'We starts here!',
      message: getStartingMessageText(),
      variablesToCollect: [],
      toolsToUse: [],
      buttons: [],
      useExactMessage: false,
    };
  }

  if (nodeType === 'Action') {
    return {
      ...baseData,
      nodeType: 'Action',
      message: '',
      useExactMessage: false,
      variablesToCollect: [],
      actionType: 'activateTool',
      toolSource: 'storedTool',
      selectedToolId: null,
      inlineTool: { toolName: '', description: '', requiredParams: [], optionalParams: [], functionConfig: '' },
      toolInputMappings: {},
      config: {}
    };
  }

  return {
    ...baseData,
    nodeType: nodeType,
    message: '',
    variablesToCollect: [],
    toolsToUse: [], 
    buttons: [],
    useExactMessage: false,
  };
}

function buildRootNode(rootNode = null) {
  const existingData = rootNode !== null && rootNode !== undefined && rootNode.data !== undefined && rootNode.data !== null ? rootNode.data : {};
  const rootData = {
    ...createBaseNodeData('We starts here!', 'Root', true),
    ...existingData,
    nodeType: 'Root',
    isRoot: true
  };
  if (typeof rootData.message !== 'string' || rootData.message.trim().length === 0) {
    rootData.message = getStartingMessageText();
  }
  return {
    id: rootNode !== null && rootNode !== undefined && typeof rootNode.id === 'string' && rootNode.id.trim().length > 0 ? rootNode.id : 'root-0',
    type: 'Root',
    position: rootNode !== null && rootNode !== undefined && rootNode.position !== undefined && rootNode.position !== null ? rootNode.position : { x: 50, y: 250 },
    label: rootData.label !== undefined && rootData.label !== null && String(rootData.label).trim().length > 0 ? String(rootData.label).trim() : 'We starts here!',
    data: rootData,
    draggable: true,
    connectable: true,
    deletable: false
  };
}

function normalizeElementsWithRoot(elementsToNormalize) {
  const sourceElements = Array.isArray(elementsToNormalize) === true ? elementsToNormalize : [];
  const normalized = [];
  let rootNode = null;
  for (let i = 0; i < sourceElements.length; i = i + 1) {
    const el = sourceElements[i];
    const isNode = el !== undefined && el !== null && el.position !== undefined && el.position !== null;
    const isRoot = isNode === true && ((el.data !== undefined && el.data !== null && el.data.nodeType === 'Root') || el.type === 'Root' || el.id === 'root-0');
    if (isRoot === true) {
      if (rootNode === null) {
        rootNode = el;
      }
      continue;
    }
    normalized.push(el);
  }
  normalized.unshift(buildRootNode(rootNode));
  return normalized;
}

function syncVueFlowFromElements() {
  const nodesArr = elements.value.filter(el => el.position !== undefined && el.position !== null);
  const edgesArr = elements.value.filter(el => el.source !== undefined && el.source !== null);
  setNodes(nodesArr);
  setEdges(edgesArr);
}

function removeEndCallOutgoingEdges(elementsToClean) {
  const sourceElements = Array.isArray(elementsToClean) === true ? elementsToClean : [];
  const endCallNodeIds = new Set();
  for (let i = 0; i < sourceElements.length; i = i + 1) {
    const el = sourceElements[i];
    if (el !== undefined && el !== null && el.position !== undefined && el.position !== null && el.data !== undefined && el.data !== null && el.data.actionType === 'endCall') {
      endCallNodeIds.add(el.id);
    }
  }
  return sourceElements.filter(el => {
    if (el !== undefined && el !== null && el.source !== undefined && el.source !== null && endCallNodeIds.has(el.source) === true) {
      return false;
    }
    return true;
  });
}

function syncStartingMessageFromRoot() {
  const rootNode = elements.value.find(el => el !== undefined && el !== null && el.data !== undefined && el.data !== null && el.data.nodeType === 'Root');
  if (rootNode === undefined || rootNode === null) {
    return;
  }
  const rootMessage = typeof rootNode.data.message === 'string' ? rootNode.data.message : '';
  emit('update:startingMessage', buildStartingMessagePayload(rootMessage));
}

function addRootNodeIfNeeded() {
  elements.value = normalizeElementsWithRoot(elements.value);
  flowKey.value++;
  nextTick(() => {
    syncVueFlowFromElements();
  });
}

function addNode(type = 'Question') {
  const newNodeId = getNextId();
  let nodeInitialData;

  const definition = availableNodes.find(n => n.type === type);
  if (definition !== undefined && definition !== null) {
    nodeInitialData = { ...definition.initialData, nodeType: type };
  } else if (type === 'Question') {
    nodeInitialData = { label: 'Question Step', nodeType: 'Question', message: '', variablesToCollect: [], toolsToUse: [], buttons: [], useExactMessage: false };
  } else if (type === 'Action') {
    nodeInitialData = { label: 'Action Step', nodeType: 'Action', message: '', stepDescription: '', actionType: 'activateTool', toolSource: 'storedTool', selectedToolId: null, inlineTool: { toolName: '', description: '', requiredParams: [], optionalParams: [], functionConfig: '' }, toolInputMappings: {}, variablesToCollect: [], useExactMessage: false, config: {} };
  } else if (type === 'KnowledgeBase') {
    nodeInitialData = { label: 'Knowledge base step', nodeType: 'KnowledgeBase' };
  } else if (type === 'If') {
    nodeInitialData = { label: 'If Condition', nodeType: 'If', stepDescription: '', config: { sourceType: 'variable', sourceKey: '', operator: 'equals', left: '', right: '' } };
  } else {
    nodeInitialData = { label: 'New Step', nodeType: type };
  }
  
  const newNode = {
    id: newNodeId,
    type: type,
    position: { x: Math.random() * 400 + 100, y: Math.random() * 200 + 50 },
    label: nodeInitialData.label,
    data: nodeInitialData,
    draggable: true,
  };
  addNodes([newNode]);
}

function buildNodeFromDefinition(nodeDef, position) {
  const nodeData = JSON.parse(JSON.stringify(nodeDef.data !== undefined && nodeDef.data !== null ? nodeDef.data : {}));
  const nodeLabel = nodeData.label !== undefined && nodeData.label !== null && String(nodeData.label).trim().length > 0
    ? String(nodeData.label).trim()
    : (nodeDef.label !== undefined && nodeDef.label !== null && String(nodeDef.label).trim().length > 0 ? String(nodeDef.label).trim() : 'New Step');
  return {
    id: getNextId(),
    type: nodeDef.type,
    position,
    label: nodeLabel,
    data: { ...nodeData, label: nodeLabel },
    draggable: true,
  };
}

function addSupportedNode(nodeDef) {
  if (nodeDef === undefined || nodeDef === null) {
    return;
  }
  const newNode = buildNodeFromDefinition(nodeDef, { x: Math.random() * 400 + 100, y: Math.random() * 200 + 50 });
  addNodes([newNode]);
}

function onDragStart(event, nodeTypeOrDef) {
  if (event.dataTransfer !== undefined && event.dataTransfer !== null) {
    if (typeof nodeTypeOrDef === 'string') {
      event.dataTransfer.setData('application/vueflow', JSON.stringify({ nodeType: nodeTypeOrDef }));
    } else {
      event.dataTransfer.setData('application/vueflow', JSON.stringify({ nodeDef: nodeTypeOrDef }));
    }
    event.dataTransfer.effectAllowed = 'move';
  }
  dragData.value = typeof nodeTypeOrDef === 'string' ? { nodeType: nodeTypeOrDef } : { nodeDef: nodeTypeOrDef };
}

function onDrop(event) {
  event.preventDefault();
  const flowContainer = event.currentTarget;
  const { left, top } = flowContainer.getBoundingClientRect();
  let nodeTypeToDrop;
  let nodeDefToDrop = null;
  try {
    const jsonData = event.dataTransfer.getData('application/vueflow');
    if (jsonData !== undefined && jsonData !== null && jsonData.length > 0) {
        const parsedData = JSON.parse(jsonData);
        nodeTypeToDrop = parsedData.nodeType;
        nodeDefToDrop = parsedData.nodeDef !== undefined ? parsedData.nodeDef : null;
    } else if (dragData.value !== null) {
        nodeTypeToDrop = dragData.value.nodeType;
        nodeDefToDrop = dragData.value.nodeDef !== undefined ? dragData.value.nodeDef : null;
    }
  } catch (error) {
    if (dragData.value !== null) { 
        nodeTypeToDrop = dragData.value.nodeType;
        nodeDefToDrop = dragData.value.nodeDef !== undefined ? dragData.value.nodeDef : null;
    }
  }

  if ((nodeTypeToDrop === undefined || nodeTypeToDrop === null) && nodeDefToDrop === null) {
    dragData.value = null;
    return;
  }

  const position = project({
    x: event.clientX - left,
    y: event.clientY - top,
  });

  if (nodeDefToDrop !== null) {
    addNodes([buildNodeFromDefinition(nodeDefToDrop, position)]);
    dragData.value = null;
    return;
  }

  const newNodeId = getNextId();
  let nodeInitialData;
  const definition = availableNodes.find(n => n.type === nodeTypeToDrop && (n.actionType === undefined || n.actionType === null));

  if (definition !== undefined && definition !== null) {
    nodeInitialData = { ...definition.initialData, nodeType: nodeTypeToDrop };
  } else {
    nodeInitialData = { label: 'New Step', nodeType: nodeTypeToDrop };
  }
  
  const newNode = {
    id: newNodeId,
    type: nodeTypeToDrop,
    position,
    label: nodeInitialData.label,
    data: nodeInitialData,
    draggable: true,
  };

  addNodes([newNode]);
  dragData.value = null;
}

onConnect((params) => {
  const sourceNode = findNode(params.source);
  if (sourceNode !== undefined && sourceNode !== null && sourceNode.data !== undefined && sourceNode.data !== null && sourceNode.data.actionType === 'endCall') {
    return;
  }
  const newEdge = { 
    id: `e-${params.source}-${params.target}-${new Date().getTime()}`, 
    ...params, 
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, width: 30, height: 30, color: '#bbb' },
    style: { stroke: '#bbb', strokeWidth: 1 },
    data: { ifCondition: '' },
    label: '' 
  };
  addEdges([newEdge]);
});

function openNodeInspector(node) {
  if (node === undefined || node === null || node.data === undefined || node.data === null) {
    return;
  }
  editingEdgeId.value = null;
  currentEdgeCondition.value = '';
  editingNodeId.value = node.id;
  editingNodeData.value = JSON.parse(JSON.stringify(node.data));
  inspectorMode.value = editingNodeData.value.nodeType === 'Action' ? 'action' : 'question';
}

function isMultiSelectionKeyPressed(event) {
  if (event === undefined || event === null) {
    return false;
  }
  if (event.ctrlKey === true) {
    return true;
  }
  if (event.metaKey === true) {
    return true;
  }
  if (event.shiftKey === true) {
    return true;
  }
  return false;
}

function handleNodeSelect(event) {
  const nativeEvent = event !== undefined && event !== null && event.event !== undefined && event.event !== null ? event.event : null;
  if (isMultiSelectionKeyPressed(nativeEvent) === true) {
    clearInspector();
    return;
  }
  openNodeInspector(event !== undefined && event !== null ? event.node : null);
}

function handleNodeDoubleClick(event) {
  openNodeInspector(event !== undefined && event !== null ? event.node : null);
}

function openEdgeInspector(edge, event = null) {
  if (edge === undefined || edge === null) {
    return;
  }
  if (event !== null && event !== undefined) {
    event.stopPropagation();
    event.preventDefault();
  }
  editingNodeId.value = null;
  editingNodeData.value = {};
  editingEdgeId.value = edge.id;
  if (edge.data !== undefined && edge.data !== null && edge.data.ifCondition !== undefined && edge.data.ifCondition !== null && edge.data.ifCondition !== '') {
    currentEdgeCondition.value = edge.data.ifCondition;
  } else {
    currentEdgeCondition.value = '';
  }
  inspectorMode.value = 'edge';
}

function handleEdgeClick({ edge, event }) {
  openEdgeInspector(edge, event);
}

function handleEdgeDoubleClick({ edge, event }) {
  openEdgeInspector(edge, event);
}

function clearInspector() {
  inspectorMode.value = 'none';
  editingNodeId.value = null;
  editingNodeData.value = {};
  editingEdgeId.value = null;
  currentEdgeCondition.value = '';
}

function saveEdgeCondition(condition) {
    if (editingEdgeId.value !== null) {
        const edge = findEdge(editingEdgeId.value);
        if (edge !== undefined && edge !== null) {
            edge.label = condition;
            if (edge.data === undefined || edge.data === null) {
                edge.data = {}
            }
            edge.data.ifCondition = condition;
            
            elements.value = elements.value.map(el => {
                if (el.id === editingEdgeId.value && el.source !== undefined && el.target !== undefined) {
                    const newData = el.data !== undefined && el.data !== null ? { ...el.data, ifCondition: condition } : { ifCondition: condition }
                    return { ...el, label: condition, data: newData }
                }
                return el;
            });
        }
    }
    currentEdgeCondition.value = condition;
}

function handleDeleteEdgeFromModal() {
    if (editingEdgeId.value !== null) {
        removeEdges([editingEdgeId.value]);
    }
    clearInspector();
}

function requestDeleteSelectedElements() {
  const selectedNodes = getSelectedNodes.value.filter(node => node.data?.nodeType !== 'Root'); 
  const selectedEdges = getSelectedEdges.value;

  if (selectedNodes.length === 0 && selectedEdges.length === 0) return;

  elementsToDelete.value = { 
    nodes: selectedNodes.map(n => n.id), 
    edges: selectedEdges.map(e => e.id) 
  };
  isDeleteConfirmOpen.value = true;
}

function confirmDelete() {
  if (elementsToDelete.value.nodes.length > 0) {
    removeNodes(elementsToDelete.value.nodes);
  }
  if (elementsToDelete.value.edges.length > 0) {
    removeEdges(elementsToDelete.value.edges);
  }
  clearInspector();
  cancelDelete();
}

function cancelDelete() {
  isDeleteConfirmOpen.value = false;
  elementsToDelete.value = { nodes: [], edges: [] };
}

function handleKeyDown(event) {
  if (isBuilderOpen.value !== true) {
    return;
  }
  const targetElement = event.target;
  const tagName = targetElement !== undefined && targetElement !== null && targetElement.tagName !== undefined ? targetElement.tagName.toLowerCase() : '';
  const insideMonaco = targetElement !== undefined && targetElement !== null && typeof targetElement.closest === 'function' ? targetElement.closest('.monaco-editor') : null;
  const isTextInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || insideMonaco !== null;
  if (isTextInput === true) {
    return;
  }
  if (event.key === 'Escape') {
    if (inspectorMode.value !== 'none') {
      clearInspector();
      return;
    }
    handleBuilderClose();
    return;
  }
  if (event.key === 'Delete' || event.key === 'Backspace') {
    requestDeleteSelectedElements();
  }
}

async function fetchAvailableTools() {
  try {
    const response = await axios.get(`${apiEndpoint.value}/api/tools/getAllAITools`);
    availableTools.value = response.data;
  } catch (error) {
  }
}

function getFlowData() {
    elements.value = normalizeElementsWithRoot(elements.value);
    elements.value = removeEndCallOutgoingEdges(elements.value);
    return elements.value.map(el => {
        if (el.position !== undefined && el.position !== null) {
            return {
                id: el.id,
                type: el.type, 
                position: el.position,
                label: el.data.label, 
                data: { ...el.data },
                draggable: el.draggable,
            };
        } else {
            return {
                id: el.id,
                source: el.source,
                target: el.target,
                type: el.type,
                label: el.label,
                data: { ...el.data }
            };
        }
    });
}

function loadFlowData(flowData) {
  if (flowData === undefined || flowData === null || Array.isArray(flowData) === false) {
    elements.value = normalizeElementsWithRoot([]);
    flowKey.value++;
    nextTick(() => {
      syncVueFlowFromElements();
      nextTick(() => fitView({ maxZoom: 1 }));
    });
    return;
  }

  const nodesArr = [];
  const edgesArr = [];
  let maxNumericId = 0;

  for (let i = 0; i < flowData.length; i = i + 1) {
    const el = flowData[i];

    if (el.source !== undefined && el.target !== undefined) {
      edgesArr.push({
        ...el,
        type: (el.type !== undefined && el.type !== null && el.type !== '') ? el.type : 'smoothstep',
        markerEnd: (el.markerEnd !== undefined && el.markerEnd !== null) ? el.markerEnd : { type: MarkerType.ArrowClosed, width: 30, height: 30, color: '#bbb' },
        style: (el.style !== undefined && el.style !== null) ? el.style : { stroke: '#bbb', strokeWidth: 1 },
      });
      continue;
    }

    if (el.position !== undefined && el.position !== null) {
      nodesArr.push(el);
      if (typeof el.id === 'string' && el.id.startsWith('step-') === true) {
        const numPart = parseInt(el.id.split('-')[1], 10);
        if (isNaN(numPart) === false && numPart > maxNumericId) {
          maxNumericId = numPart;
        }
      }
    }
  }

  id = maxNumericId + 1;

  elements.value = removeEndCallOutgoingEdges(normalizeElementsWithRoot([...nodesArr, ...edgesArr]));
  syncStartingMessageFromRoot();
  flowKey.value++;

  nextTick(() => {
    syncVueFlowFromElements();
    nextTick(() => fitView({ maxZoom: 1 }));
  });
}

defineExpose({
  getFlowData,
  loadFlowData,
  fitView,
  openFlowBuilder,
  closeFlowBuilder
});

onMounted(() => {
  fetchAvailableTools();
  addRootNodeIfNeeded();
});

watch(() => props.startingMessage, () => {
  const startingText = getStartingMessageText();
  if (startingText.length === 0) {
    return;
  }
  let changed = false;
  elements.value = normalizeElementsWithRoot(elements.value).map(el => {
    const isRoot = el !== undefined && el !== null && el.data !== undefined && el.data !== null && el.data.nodeType === 'Root';
    if (isRoot === true) {
      const currentMessage = typeof el.data.message === 'string' ? el.data.message.trim() : '';
      if (currentMessage.length === 0) {
        changed = true;
        return {
          ...el,
          data: {
            ...el.data,
            message: startingText
          }
        };
      }
    }
    return el;
  });
  if (changed === true) {
    flowKey.value++;
    nextTick(() => {
      syncVueFlowFromElements();
    });
  }
}, { deep: true });

watch(isBuilderOpen, (isOpen) => {
  if (isOpen === true) {
    nextTick(() => {
      nextTick(() => fitView({ maxZoom: 1 }));
    });
  }
});

function closeStepModal() {
  clearInspector();
}

function closeActionStepModal() {
  clearInspector();
}

function saveStepData(updatedData) {
  if (editingNodeId.value !== null) {
    const nodeToUpdate = findNode(editingNodeId.value);
    if (nodeToUpdate !== undefined && nodeToUpdate !== null) {
      const isRootUpdate = updatedData !== undefined && updatedData !== null && updatedData.nodeType === 'Root';
      
      elements.value = elements.value.map(el => {
        if (el.id === editingNodeId.value) {
          const newLabel = (updatedData.label !== undefined && updatedData.label !== null && updatedData.label.trim().length > 0) ? updatedData.label : `Step ${el.id}`;
          return {
            ...el,
            label: newLabel,
            data: { 
              ...el.data, 
              ...updatedData, 
              label: newLabel 
            },
          };
        }
        return el;
      });
      elements.value = removeEndCallOutgoingEdges(normalizeElementsWithRoot(elements.value));
      if (isRootUpdate === true) {
        const rootMessage = typeof updatedData.message === 'string' ? updatedData.message : '';
        emit('update:startingMessage', buildStartingMessagePayload(rootMessage));
      }
      editingNodeData.value = {
        ...updatedData,
        label: (updatedData.label !== undefined && updatedData.label !== null && updatedData.label.trim().length > 0) ? updatedData.label : (nodeToUpdate.data !== undefined && nodeToUpdate.data !== null ? nodeToUpdate.data.label : 'Step')
      };
      nextTick(() => {
        syncVueFlowFromElements();
      });
    }
  }
}

function openFlowBuilder() {
  isBuilderOpen.value = true;
  nextTick(() => {
    nextTick(() => fitView({ maxZoom: 1 }));
  });
}

function handleBuilderClose() {
  closeFlowBuilder();
  emit('builder-closed');
}

function closeFlowBuilder() {
  isBuilderOpen.value = false;
  clearInspector();
}

function centerCanvas() {
  nextTick(() => fitView({ maxZoom: 1 }));
}

function organizeFlow() {
    const nodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
    const edges = elements.value.filter(el => el.source !== undefined && el.source !== null);

    const rootNode = nodes.find(n => n.type === 'Root' || (n.data !== undefined && n.data !== null && n.data.nodeType === 'Root'));
    if (rootNode === undefined || rootNode === null) {
        return;
    }

    const adj = new Map();
    for (let i = 0; i < nodes.length; i = i + 1) {
        adj.set(nodes[i].id, []);
    }
    for (let i = 0; i < edges.length; i = i + 1) {
        const edge = edges[i];
        if (adj.has(edge.source) === true) {
            adj.get(edge.source).push(edge.target);
        }
    }

    const xSpacing = 300;
    const ySpacing = 150;
    const nodeHeightEstimate = 100;

    const newPositions = new Map();
    const levels = new Map();
    const nodesAtLevel = new Map();

    const queue = [{ nodeId: rootNode.id, level: 0 }];
    const visited = new Set([rootNode.id]);
    levels.set(rootNode.id, 0);

    if (nodesAtLevel.has(0) === false) {
        nodesAtLevel.set(0, []);
    }
    nodesAtLevel.get(0).push(rootNode.id);

    let head = 0;
    while(head < queue.length) {
        const item = queue[head];
        head = head + 1;
        const nodeId = item.nodeId;
        const level = item.level;
        const children = adj.get(nodeId) || [];
        for (let i = 0; i < children.length; i = i + 1) {
            const childId = children[i];
            const childNode = findNode(childId);
            if (childNode !== undefined && childNode !== null && visited.has(childId) === false) {
                visited.add(childId);
                levels.set(childId, level + 1);
                if (nodesAtLevel.has(level + 1) === false) {
                    nodesAtLevel.set(level + 1, []);
                }
                nodesAtLevel.get(level + 1).push(childId);
                queue.push({ nodeId: childId, level: level + 1 });
            }
        }
    }
    
    const canvasElement = document.querySelector('.llm-flow-canvas');
    const canvasCenterY = canvasElement !== null ? canvasElement.clientHeight / 2 : 300;

    nodesAtLevel.forEach((nodesInLevel, level) => {
        const x = 100 + level * xSpacing;
        const numNodes = nodesInLevel.length;
        const levelHeight = numNodes * nodeHeightEstimate + Math.max(0, numNodes - 1) * ySpacing;
        let currentY = canvasCenterY - levelHeight / 2;

        for (let i = 0; i < nodesInLevel.length; i = i + 1) {
            const nodeId = nodesInLevel[i];
            newPositions.set(nodeId, { x, y: currentY + nodeHeightEstimate / 2 });
            currentY = currentY + nodeHeightEstimate + ySpacing;
        }
    });

    elements.value = elements.value.map(el => {
        if (el.position !== undefined && el.position !== null && newPositions.has(el.id) === true) {
            return { ...el, position: newPositions.get(el.id) };
        }
        return el;
    });

    nextTick(() => {
        fitView({ maxZoom: 1 });
    });
}

</script>

<style scoped>
.llm-flow-container {
  height: 60vh; 
  display: flex;
  flex-direction: column;
  outline: none; 
  position: relative;
}

.llm-flow-canvas {
  flex-grow: 1;
  border: 1px solid #ccc;
  background-color: #f8f8f8;
}

.llm-flow-workspace {
  flex: 1;
  min-height: 0;
  display: flex;
  direction: ltr;
}

.llm-flow-canvas-wrap {
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.llm-flow-sidebar {
  width: 320px;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  min-height: 0;
  direction: rtl;
}

.llm-flow-sidebar-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: right;
}

.llm-flow-sidebar-actions {
  display: flex;
  align-items: center;
}

.llm-flow-sidebar-actions > * + * {
  margin-right: 0.5rem;
}

.controls-bottom {
  padding: 10px;
  display: flex;
  
  border-top: 1px solid #ccc;
}
.controls-bottom > * + * { margin-left: 10px; }

.edge-condition-card {
  background: white;
  padding: 4px 8px;
  border-radius: 15px; 
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  font-size: 10px;
  border: 1px solid #ccc;
  cursor: pointer; 
  white-space: nowrap; 
}

.edge-condition-card.no-condition {
  color: #888; 
  border-style: dashed;
}

:deep(.vue-flow__node-default) {
  border-radius: 12px; 
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
}

:deep(.vue-flow__node-root) {
  border-radius: 12px;
  background-color: #f7fafc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #d1d5db;
}

.node-type-tag {
  background-color: #f1f5f9;
  color: #475569;
  font-size: 0.58rem;
  padding: 2px 6px;
  border-radius: 3px;
  margin-bottom: 4px;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 600;
}

.custom-node-padding {
  padding: 6px 10px;
}

.node-description {
  font-size: 0.72rem;
  color: #4b5563;
  margin-top: 6px;
  white-space: pre-wrap;
  background-color: #f8fafc;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px dashed #cbd5e1;
  text-align: left;
  min-width: 220px;
  max-width: 340px;
  overflow-wrap: anywhere;
  word-break: break-all;
  overflow: hidden;
}

:deep(.vue-flow__handle) {
  width: 10px;
  height: 10px; 
}


:deep(.vue-flow__node.selected) {
  border: 2px solid #3b82f6; 
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4); 
  border-radius: 12px;
}

:deep(.vue-flow__node-default.selected),
:deep(.vue-flow__node-Root.selected),
:deep(.vue-flow__node-Question.selected),
:deep(.vue-flow__node-Action.selected),
:deep(.vue-flow__node-KnowledgeBase.selected) {
  border-width: 2px; 
  border-color: #3b82f6 !important; 
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4) !important; 
  border-radius: 12px !important;
}

.llm-flow-entry {
  width: 100%;
}

.llm-flow-launcher-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem 1.15rem;
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.08), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
}

.llm-flow-launcher-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.llm-flow-launcher-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.2rem 0.45rem;
  border-radius: 0.2rem;
  background: #f1f5f9;
  color: #334155;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.llm-flow-launcher-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
}

.llm-flow-launcher-description {
  margin: 0;
  max-width: 52rem;
  color: #475569;
  line-height: 1.7;
}

.llm-flow-launcher-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.llm-flow-launcher-stats--compact {
  justify-content: flex-end;
}

.llm-flow-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.28rem 0.55rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: #f1f5f9;
  color: #334155;
  font-size: 0.7rem;
  font-weight: 600;
}

.llm-flow-launcher-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.llm-flow-fullscreen-shell {
  position: fixed;
  inset: 0;
  z-index: 1100;
}

.llm-flow-fullscreen-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.36);
  backdrop-filter: blur(6px);
}

.llm-flow-fullscreen {
  position: absolute;
  inset: 0.6rem;
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  background: #f1f5f9;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);
  overflow: hidden;
  outline: none;
}

.llm-flow-fullscreen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.llm-flow-fullscreen-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.llm-flow-fullscreen-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) 360px;
  gap: 0.5rem;
  padding: 0.5rem;
  direction: ltr;
}

.llm-flow-library,
.llm-flow-inspector,
.llm-flow-stage {
  min-height: 0;
}

.llm-flow-library,
.llm-flow-inspector {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04);
}

.llm-flow-stage {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.llm-flow-stage-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 1.25rem;
  background: #ffffff;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.05);
}

.llm-flow-stage-toolbar-copy h3,
.llm-flow-panel-header h3 {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
}

.llm-flow-stage-toolbar-copy p {
  margin: 0.35rem 0 0;
  color: #64748b;
  line-height: 1.5;
}

.llm-flow-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  border-bottom: 1px solid #e2e8f0;
  background: #fafbfc;
}

.llm-flow-panel-eyebrow {
  margin-bottom: 0.2rem;
  font-size: 0.65rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.llm-flow-inspector {
  direction: rtl;
}

.llm-flow-inspector-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0.5rem;
  background: #f8fafc;
}

.llm-flow-inspector-stack {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  height: 100%;
  min-height: 0;
}

.llm-flow-selection-card {
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  padding: 0.5rem 0.6rem;
  background: #ffffff;
  box-shadow: none;
}

.llm-flow-selection-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}

.llm-flow-selection-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.4rem;
  border-radius: 0.2rem;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.65rem;
  font-weight: 600;
}

.llm-flow-selection-meta {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 500;
}

.llm-flow-selection-card h4 {
  margin: 0 0 0.2rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
}

.llm-flow-selection-card p {
  margin: 0;
  color: #64748b;
  line-height: 1.45;
  font-size: 0.75rem;
}

.llm-flow-inspector-body :deep(.app-modal-panel-shell) {
  height: 100%;
  min-height: 0;
  flex: 1;
}

.llm-flow-inspector-body :deep(.app-modal-panel-plain-shell) {
  height: 100%;
  min-height: 0;
  flex: 1;
}

.llm-flow-inspector-body :deep(.step-editor-shell),
.llm-flow-inspector-body :deep(.action-step-editor-shell) {
  height: 100%;
  min-height: 0;
}

.llm-flow-canvas-wrap {
  flex: 1;
  min-height: 0;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  background: #ffffff;
  overflow: hidden;
  box-shadow: none;
}

.llm-flow-canvas {
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.05), transparent 20%),
    #f8fafc;
}

.edge-condition-card {
  background: white;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  font-size: 0.68rem;
  border: 1px solid #dbe2ea;
  cursor: pointer;
  white-space: nowrap;
  color: #0f172a;
  font-weight: 600;
}

.edge-condition-card.no-condition {
  color: #64748b;
  border-style: dashed;
}

::deep(.vue-flow__node-default) {
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  border: 1px solid #e2e8f0;
}

::deep(.vue-flow__node-root) {
  border-radius: 12px;
  background-color: #f7fafc;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  border: 1px solid #d1d5db;
}

.node-type-tag {
  background-color: #f1f5f9;
  color: #475569;
  font-size: 0.58rem;
  padding: 2px 6px;
  border-radius: 3px;
  margin-bottom: 4px;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 600;
}

.custom-node-padding {
  padding: 6px 10px;
}

.node-description {
  font-size: 0.72rem;
  color: #4b5563;
  margin-top: 6px;
  white-space: pre-wrap;
  background-color: #f8fafc;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px dashed #cbd5e1;
  text-align: left;
  min-width: 220px;
  max-width: 340px;
  overflow-wrap: anywhere;
  word-break: break-all;
  overflow: hidden;
}

::deep(.vue-flow__handle) {
  width: 10px;
  height: 10px;
}

::deep(.vue-flow__node.selected) {
  border: 2px solid #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4);
  border-radius: 12px;
}

::deep(.vue-flow__node-default.selected),
::deep(.vue-flow__node-Root.selected),
::deep(.vue-flow__node-Question.selected),
::deep(.vue-flow__node-Action.selected),
::deep(.vue-flow__node-KnowledgeBase.selected) {
  border-width: 2px;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4) !important;
  border-radius: 12px !important;
}

.llm-flow-empty-inspector {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  padding: 1rem;
  color: #64748b;
}

.llm-flow-empty-inspector-icon {
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
}

.llm-flow-empty-inspector h4 {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
}

.llm-flow-empty-inspector p {
  margin: 0;
  max-width: 16rem;
  line-height: 1.45;
  font-size: 0.75rem;
}

.llm-flow-edge-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  padding: 0.5rem;
}

.llm-flow-edge-panel-actions {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: auto;
}

@media (max-width: 1400px) {
  .llm-flow-fullscreen-body {
    grid-template-columns: 220px minmax(0, 1fr) 320px;
  }
}

@media (max-width: 1100px) {
  .llm-flow-launcher-card,
  .llm-flow-fullscreen-header {
    flex-direction: column;
    align-items: stretch;
  }

  .llm-flow-fullscreen {
    inset: 0.5rem;
  }

  .llm-flow-fullscreen-body {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(240px, auto) minmax(420px, 1fr) minmax(320px, auto);
    overflow: auto;
  }

  .llm-flow-library,
  .llm-flow-inspector,
  .llm-flow-stage {
    min-height: 18rem;
  }
}

.llm-flow-delete-confirm {
  position: fixed;
  z-index: 12000;
  max-width: 100%;
  max-height: 100%;
}
</style> 